import React, { useState, useEffect } from "react";
import { Icon } from "../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

/* ============================================================
   Talention — Dashboard screen
   Glass widgets · solid content inside
   ============================================================ */

function Dashboard() {
  const { state } = useStore();
  const { positions, departments, factors, scales, entity } = state;

  const total = positions.length;
  const finalized = positions.filter(p => p.status === "FINALIZADA").length;
  const inProgress = positions.filter(p => p.status === "EN PROGRESO").length;
  const pending = total - finalized - inProgress;
  const completion = total ? Math.round((finalized / total) * 100) : 0;

  /* dist by department */
  const DEPT_COLORS = ["#0a84ff", "#af52de", "#34c759", "#ff9f0a", "#ff3b30", "#5856d6"];
  const deptDist = departments.map((d, i) => ({
    label: d.name.split(" ")[0],
    fullName: d.name,
    value: positions.filter(p => p.departmentId === d.id).length,
    color: DEPT_COLORS[i % DEPT_COLORS.length],
  }));
  const deptTotal = deptDist.reduce((s, d) => s + d.value, 0) || 1;

  /* dist by scale */
  const scaleDist = scales.map(s => {
    const count = positions.filter(p => {
      if (p.status !== "FINALIZADA") return false;
      return p.points >= s.min && p.points <= s.max;
    }).length;
    return { ...s, count };
  }).filter(s => s.count > 0).sort((a, b) => b.count - a.count);
  const scaleTotal = scaleDist.reduce((s, d) => s + d.count, 0) || 1;

  const totalSVPT = factors.groups.reduce((s, g) => s + g.svpt, 0);

  /* alerts */
  const alerts = [];
  if (totalSVPT !== 100) {
    alerts.push({ tone: "warn", icon: "alert", text: `Los pesos SVPT suman ${totalSVPT}%`, sub: `Debes ajustarlos para que sumen 100%`, action: "Revisar", to: "/factors" });
  }
  if (pending > 0) {
    alerts.push({ tone: "info", icon: "clock", text: `${pending} puesto${pending !== 1 ? "s" : ""} pendiente${pending !== 1 ? "s" : ""}`, sub: "Sin valoración iniciada", action: "Valorar", to: "/valuation" });
  }
  if (inProgress > 0) {
    alerts.push({ tone: "info", icon: "play", text: `${inProgress} valoración${inProgress !== 1 ? "es" : ""} en progreso`, sub: "Continúa donde lo dejaste", action: "Continuar", to: "/valuation" });
  }
  if (alerts.length === 0) {
    alerts.push({ tone: "good", icon: "circle-check", text: "Todo en orden", sub: "Sistema configurado correctamente." });
  }

  const topPositions = positions
    .filter(p => p.status === "FINALIZADA")
    .sort((a, b) => b.points - a.points)
    .slice(0, 5);

  const avgPoints = finalized > 0
    ? Math.round(positions.filter(p => p.status === "FINALIZADA").reduce((s, p) => s + p.points, 0) / finalized)
    : 0;
  const avgScale = TALENTION_HELPERS.findScaleForPoints(avgPoints, scales);

  return (
    <div className="stack stagger" style={{ gap: 22 }}>
      <PageHead
        eyebrow="Visión global"
        title={`Bienvenido, José Luis`}
        sub={`${entity.name} · ${new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`}
        actions={
          <div className="row">
            <button className="btn btn-secondary"><Icon name="download" size={14} /> Exportar</button>
            <button className="btn btn-primary" onClick={() => window.GO("/valuation")}>
              <Icon name="plus" size={14} /> Nueva valoración
            </button>
          </div>
        }
      />

      {/* KPI row */}
      <div className="grid grid-4 kpi-grid">
        <KPI label="Puestos registrados" value={total} sub={`${departments.length} departamentos`} icon="briefcase" color="#0a84ff" delay={0} />
        <KPI label="Valoraciones finalizadas" value={finalized} sub={`${completion}% del total`} icon="circle-check" color="#34c759" trend={{ value: `${completion}%`, dir: "up" }} delay={60} />
        <KPI label="En curso / pendientes" value={pending + inProgress} sub={inProgress > 0 ? `${inProgress} en progreso` : "Sin actividad nueva"} icon="clock" color="#ff9500" delay={120} />
        <KPI label="Puntuación media" value={avgPoints} sub={avgScale ? avgScale.name + " · " + avgScale.label : "Sin valoraciones"} icon="trend-up" color="#af52de" delay={180} />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1.4fr 1fr" }}>
        {/* Progreso global */}
        <Card style={{ display: "flex", gap: 22, alignItems: "center" }}>
          <Donut value={completion} size={150} stroke={14} color="var(--ink-900)">
            <div>
              <div className="display-num" style={{ fontSize: 34 }}>{completion}<span style={{ fontSize: 18 }}>%</span></div>
              <div className="tiny muted">completado</div>
            </div>
          </Donut>
          <div style={{ flex: 1 }}>
            <div className="card-eyebrow">Progreso global de valoración</div>
            <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", marginTop: 4 }}>
              {finalized} de {total} puestos
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              Has finalizado <b>{finalized}</b> valoraciones. Quedan <b>{pending}</b> por iniciar
              {inProgress > 0 ? <> y <b>{inProgress}</b> en progreso</> : ""}.
            </div>
            <div className="row" style={{ gap: 6, marginTop: 16, flexWrap: "wrap" }}>
              <StatusPill tone="good" label="Finalizadas" count={finalized} />
              <StatusPill tone="info" label="En progreso" count={inProgress} />
              <StatusPill tone="warn" label="Pendientes" count={pending} />
            </div>
          </div>
        </Card>

        {/* Alerts */}
        <Card title="Avisos" action={<Badge tone="brand">{alerts.length}</Badge>}>
          <div className="stack" style={{ gap: 8 }}>
            {alerts.map((a, i) => (
              <div key={i} className="surface" style={{ display: "flex", gap: 12, alignItems: "center", padding: "12px 14px" }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: a.tone === "warn" ? "var(--warn-100)" : a.tone === "good" ? "var(--good-100)" : a.tone === "bad" ? "var(--bad-100)" : "var(--info-100)",
                  color: a.tone === "warn" ? "#9a5b00" : a.tone === "good" ? "#15803d" : a.tone === "bad" ? "#b91c1c" : "#0a52c2",
                  display: "grid", placeItems: "center", flexShrink: 0,
                }}><Icon name={a.icon} size={14} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="semi" style={{ fontSize: 13, letterSpacing: "-0.01em" }}>{a.text}</div>
                  {a.sub && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{a.sub}</div>}
                </div>
                {a.action && (
                  <button onClick={() => a.to && window.GO(a.to)} className="btn btn-secondary btn-sm">
                    {a.action}
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Distribución por departamento + escala */}
      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <Card title="Distribución por departamento">
          <div className="stack" style={{ gap: 6 }}>
            {deptDist.map((d, i) => {
              const pct = Math.round((d.value / deptTotal) * 100);
              return (
                <div key={i} className="surface-soft" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", boxShadow: `inset 3px 0 0 ${d.color}` }}>
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: d.color + "18", color: d.color, display: "grid", placeItems: "center", flexShrink: 0, fontWeight: 700, fontSize: 13 }}>{d.fullName[0]}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="semi" style={{ fontSize: 13.5 }}>{d.fullName}</div>
                    <div style={{ marginTop: 6, height: 5, background: "var(--ink-150)", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: d.color, borderRadius: 999, transition: "width 400ms cubic-bezier(.2,.7,.2,1)" }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 60 }}>
                    <div className="bold mono" style={{ fontSize: 15, lineHeight: 1 }}>{d.value}</div>
                    <div className="tiny muted">{pct}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="Distribución por escala salarial">
          {scaleDist.length === 0 ? (
            <div className="surface" style={{ padding: 28, textAlign: "center" }}>
              <Icon name="scale" size={28} className="muted" style={{ marginBottom: 8 }} />
              <div className="muted" style={{ fontSize: 13 }}>Sin valoraciones finalizadas todavía</div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => window.GO("/valuation")}>
                Iniciar valoración
              </button>
            </div>
          ) : (
            <div className="stack" style={{ gap: 6 }}>
              {scaleDist.map(s => {
                const pct = Math.round((s.count / scaleTotal) * 100);
                return (
                  <div key={s.id} className="surface-soft" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px" }}>
                    <span style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: s.color + "1f", color: s.color,
                      display: "grid", placeItems: "center",
                      fontWeight: 700, fontSize: 11.5,
                      letterSpacing: 0,
                      flexShrink: 0,
                    }}>{s.id < 10 ? "0" + s.id : s.id}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="semi" style={{ fontSize: 13.5 }}>{s.label}</div>
                      <div className="tiny muted">Rango {s.min}–{s.max} pts</div>
                    </div>
                    <div style={{ width: 80 }}>
                      <div style={{ height: 5, background: "var(--ink-150)", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: pct + "%", background: s.color, borderRadius: 999, transition: "width 400ms" }} />
                      </div>
                    </div>
                    <div style={{ textAlign: "right", minWidth: 36 }}>
                      <div className="bold mono" style={{ fontSize: 15, lineHeight: 1 }}>{s.count}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Top puestos + Factores actuales */}
      <div className="grid" style={{ gridTemplateColumns: "1.3fr 1fr" }}>
        <Card title="Top puestos por puntuación">
          {topPositions.length === 0 ? (
            <div className="surface" style={{ padding: 24, textAlign: "center" }}>
              <div className="muted">Aún no hay puestos finalizados</div>
            </div>
          ) : (
            <div className="stack" style={{ gap: 6 }}>
              {topPositions.map((p, i) => {
                const dept = departments.find(d => d.id === p.departmentId);
                const scale = TALENTION_HELPERS.findScaleForPoints(p.points, scales);
                return (
                  <div key={p.id} className="surface-soft" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 8, display: "grid", placeItems: "center",
                      background: i === 0 ? "#fef3c7" : i === 1 ? "#f1f5f9" : i === 2 ? "#fff7ed" : "var(--ink-50)",
                      color: i === 0 ? "#d97706" : i === 1 ? "#475569" : i === 2 ? "#9a3e07" : "var(--ink-600)",
                      fontWeight: 700, fontSize: 12,
                      border: `1px solid ${i === 0 ? "#fde68a" : i === 1 ? "#e2e8f0" : i === 2 ? "#fed7aa" : "var(--border)"}`,
                      flexShrink: 0,
                    }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="semi" style={{ fontSize: 14, letterSpacing: "-0.01em" }}>{p.name}</div>
                      <div className="tiny muted">{dept?.name} · {p.code}</div>
                    </div>
                    {scale && <Badge style={{ background: scale.color + "22", color: scale.color, border: "1px solid " + scale.color + "33" }}>{scale.name}</Badge>}
                    <div className="bold mono" style={{ fontSize: 16, minWidth: 50, textAlign: "right", letterSpacing: "-0.02em" }}>{p.points}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title="Pesos de factores" action={
          <button className="btn btn-ghost btn-sm" onClick={() => window.GO("/factors")}>Configurar</button>
        }>
          <div className="surface" style={{ padding: 16 }}>
            <div className="stack" style={{ gap: 14 }}>
              {factors.groups.map(g => (
                <div key={g.id}>
                  <div className="spread" style={{ marginBottom: 6 }}>
                    <div className="row" style={{ gap: 8 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: g.iconBg, color: g.color,
                        display: "grid", placeItems: "center",
                      }}><Icon name={g.icon} size={12} /></span>
                      <span className="semi" style={{ fontSize: 13 }}>{g.short}</span>
                    </div>
                    <span className="mono semi" style={{ fontSize: 13 }}>{g.svpt}%</span>
                  </div>
                  <div className="bar">
                    <div className="bar-fill" style={{ width: g.svpt + "%", background: g.color }} />
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                <span className="semi" style={{ fontSize: 13 }}>Total</span>
                <div className="row" style={{ gap: 6 }}>
                  {totalSVPT === 100
                    ? <Icon name="circle-check" size={14} style={{ color: "#15803d" }} />
                    : <Icon name="alert" size={14} style={{ color: "#b91c1c" }} />}
                  <span className="mono bold" style={{ fontSize: 14, color: totalSVPT === 100 ? "#15803d" : "#b91c1c" }}>{totalSVPT}%</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function PageHead({ title, sub, actions, eyebrow }) {
  return (
    <div className="page-head">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions}
    </div>
  );
}

function KPI({ label, value, sub, icon, color, trend, delay }) {
  const tint = (color || "#1d1d1f") + "1f";
  return (
    <div className="kpi-card" style={{ "--kpi-color": color, animationDelay: (delay || 0) + "ms" }}>
      <div className="kpi-head">
        <span className="kpi-label">{label}</span>
        <span className="kpi-icon" style={{ background: tint, color }}>
          <Icon name={icon} size={15} />
        </span>
      </div>
      <div className="kpi-body">
        <span className="kpi-num">{value}</span>
      </div>
      <div className="kpi-foot">
        <span className="kpi-sub">{sub}</span>
        {trend && <span className={"kpi-trend " + trend.dir}>
          <Icon name="trend-up" size={11} />
          {trend.value}
        </span>}
      </div>
    </div>
  );
}

function StatusPill({ tone, label, count }) {
  return (
    <div className="row" style={{ gap: 8, padding: "5px 11px", borderRadius: 999, background: "rgba(255,255,255,0.5)", border: "1px solid var(--border)" }}>
      <span className="dot" style={{
        background: tone === "good" ? "var(--good-500)" : tone === "warn" ? "var(--warn-500)" : "var(--info-500)",
        width: 7, height: 7,
      }} />
      <span style={{ fontSize: 12 }}>{label}</span>
      <span className="bold mono" style={{ fontSize: 12.5 }}>{count}</span>
    </div>
  );
}

export { Dashboard, PageHead, KPI, StatusPill };